import { useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const toDate = (day) => new Date(`${day}T12:00:00Z`);
const toDay = (d) => d.toISOString().slice(0, 10);
const shift = (day, n) => toDay(new Date(toDate(day).getTime() + n * 86400000));
const fmt = (day, opts) => toDate(day).toLocaleDateString("en-US", { timeZone: "UTC", ...opts }).toUpperCase();

export function DateStrip({ value, onChange, today }) {
  const [open, setOpen] = useState(false);
  const days = [-3, -2, -1, 0, 1, 2, 3].map((n) => shift(value, n));
  return (
    <div data-testid="date-strip" className="flex items-stretch rounded-lg border border-slate-800 bg-[#121722]/90 mb-6">
      <button data-testid="date-prev-week" onClick={() => onChange(shift(value, -7))} aria-label="Previous week"
        className="px-3 sm:px-4 text-blue-400 hover:text-blue-300 hover:bg-slate-800/50 rounded-l-lg transition-colors">
        <ChevronLeft className="h-6 w-6" />
      </button>
      <div className="flex-1 grid grid-cols-3 sm:grid-cols-7">
        {days.map((d, i) => {
          const active = d === value;
          return (
            <button key={d} data-testid={`date-day-${d}`} onClick={() => onChange(d)}
              className={`py-3 text-center transition-colors relative ${i < 2 || i > 4 ? "hidden sm:block" : ""} ${active ? "text-slate-50" : "text-slate-500 hover:text-slate-200"}`}>
              <div className={`text-sm ${active ? "font-extrabold" : "font-medium"}`}>{fmt(d, { weekday: "short" })}</div>
              <div className={`text-[11px] font-mono-num mt-0.5 ${active ? "font-bold" : ""}`}>{fmt(d, { month: "short", day: "2-digit" })}</div>
              {d === today && <span className="absolute left-1/2 -translate-x-1/2 bottom-1 h-1 w-1 rounded-full bg-emerald-400" />}
              {active && <span className="absolute inset-x-6 bottom-0 h-0.5 bg-blue-500 rounded" />}
            </button>
          );
        })}
      </div>
      <button data-testid="date-next-week" onClick={() => onChange(shift(value, 7))} aria-label="Next week"
        className="px-3 sm:px-4 text-blue-400 hover:text-blue-300 hover:bg-slate-800/50 transition-colors">
        <ChevronRight className="h-6 w-6" />
      </button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="date-calendar-btn" aria-label="Pick a date"
            className="px-4 sm:px-5 border-l border-slate-800 text-slate-300 hover:text-slate-50 hover:bg-slate-800/50 rounded-r-lg transition-colors">
            <CalendarDays className="h-6 w-6" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0 bg-[#121722] border-slate-700">
          <Calendar mode="single" selected={toDate(value)} defaultMonth={toDate(value)}
            onSelect={(d) => { if (d) { onChange(d.toLocaleDateString("en-CA")); setOpen(false); } }} />
          <button data-testid="date-today-btn" onClick={() => { onChange(today); setOpen(false); }}
            className="w-full py-2 text-xs font-bold uppercase tracking-widest text-blue-400 border-t border-slate-800 hover:bg-slate-800/50">Today</button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
